import { BRAND } from '@/brand/brand';

/**
 * The 3D scene's palette, DERIVED from the brand. Nothing here invents a colour.
 *
 * This indirection is the point: apply a brand in `brand.ts` and the whole world re-skins — sky,
 * fog, track, player, collectibles — without touching the game code. If you find yourself writing a
 * hex value in a scene file, it belongs in the brand instead.
 *
 * Keep the STRUCTURE when re-theming: a graded sky, fog for depth, dark hazards, one bright player,
 * one saturated collectible. That hierarchy is what keeps a runner readable at speed on a phone,
 * where detail is the enemy of legibility.
 */
export const PALETTE = {
	skyTop: BRAND.color.base,
	skyMid: BRAND.color.mid,
	skyLow: BRAND.color.highlight,

	fog: BRAND.color.mid,

	track: BRAND.color.base,
	trackEdge: BRAND.color.mid,
	laneLine: BRAND.color.danger,

	/** Flanking terrain — depth without detail. */
	ridgeFar: BRAND.color.mid,
	ridgeNear: BRAND.color.base,

	/** The player is the only bright mass on screen: that is how the eye tracks it. */
	player: BRAND.color.primary,
	playerGlow: BRAND.color.highlight,

	hazard: BRAND.color.base,
	hazardEdge: BRAND.color.danger,

	orb: BRAND.color.accent,
	orbGlow: BRAND.color.accent,
} as const;

/** Pacing. Tuned for a phone: readable at a glance, and it must fail fairly. */
export const TUNING = {
	laneCount: 3,
	laneWidth: 2.2,

	startSpeed: 14,
	maxSpeed: 34,
	/** Speed added per point. Slow enough that the ramp is felt, not suffered. */
	speedRamp: 0.055,

	gravity: -58,
	jumpVelocity: 17.5,
	/** Grace window after leaving the ground where a jump still counts. Removes the "I pressed it
	 *  and nothing happened" feeling that makes runners read as broken rather than hard. */
	coyoteTimeMs: 100,
	/** A jump pressed just before landing fires on touchdown instead of being swallowed. */
	jumpBufferMs: 130,
	/** Lane changes are eased, not teleported — instant snapping reads as cheap. */
	laneChangeMs: 130,

	spawnEveryM: 13,
	spawnEveryMinM: 6.5,

	/** Clearing a hazard closely scores more — rewards nerve over jumping early every time. */
	nearMissRadius: 1.1,
	nearMissBonus: 5,
	orbValue: 3,
} as const;
